import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@veracity/vui'

import { scenarioConstraintsClient } from '@/api'
import { DatasetFileTypes } from '@/interfaces'
import { generateRange } from '@/utils'

interface SetScenarioConstraintRequest {
  years?: number[] | undefined
  constraints: number[][]
}

const getBaseQueryKey = scenarioConstraintsClient.QUERY_KEY

export const useConstraintDesigner = (
  scenarioId: number,
  datasetFileType: DatasetFileTypes,
  isCharging: boolean,
  isBusinessDay: boolean
) => {
  const QUERY_KEY = [
    ...getBaseQueryKey(scenarioId),
    { datasetFileType, isCharging, isBusinessDay }
  ]
  const { showSuccess } = useToast()
  const queryClient = useQueryClient()

  const getConstraintsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      scenarioConstraintsClient.getConstraint(
        scenarioId,
        datasetFileType,
        isCharging,
        isBusinessDay
      )
  })

  const setConstraintMutation = useMutation({
    mutationFn: (req: SetScenarioConstraintRequest) =>
      scenarioConstraintsClient.setConstraint(scenarioId, {
        marketProductType: datasetFileType,
        isBusinessDay: isBusinessDay,
        isCharging: isCharging,
        years: req.years,
        constraints: req.constraints
      }),
    onSuccess: async () => {
      showSuccess('Constraint successfully updated')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const clearConstraintMutation = useMutation({
    mutationFn: (years: number[]) =>
      scenarioConstraintsClient.setConstraint(scenarioId, {
        marketProductType: datasetFileType,
        isBusinessDay: isBusinessDay,
        isCharging: isCharging,
        years: years,
        constraints: generateRange(0, 11).map(() => new Array(24).fill(null)) // TODO: Fix that later - wrong type on BackendGenerated.ts
      }),
    onSuccess: async () => {
      showSuccess('Constraint successfully updated')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  return {
    constraintInfo: getConstraintsQuery.data,
    isFetching: getConstraintsQuery.isFetching,
    setConstraint: setConstraintMutation.mutate,
    setConstraintInProgress: setConstraintMutation.isPending,
    clearConstraint: clearConstraintMutation.mutate
  }
}
